/**
 * Aggregate token/cost usage for a Meridian chat (from meridian_usage_events).
 */

import { getSupabaseClient } from '../../supabase-client';
import { logger } from '../../../logger';
import {
  CHARS_PER_TOKEN_ESTIMATE,
  resolveContextWindowConfig,
} from '../../../bedrock/context-window';

export interface ChatUsageAggregate {
  chatId: string;
  stepCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheCreationInputTokens: number;
  cacheReadInputTokens: number;
  totalCostUsd: number;
  /** Soft limits the model path uses for prepareStep trimming */
  contextWindow: {
    maxMessages: number;
    maxChars: number;
    estimatedMaxTokens: number;
    charsPerTokenEstimate: number;
  };
}

export async function getChatUsageAggregate(
  chatId: string
): Promise<ChatUsageAggregate> {
  try {
    const windowConfig = resolveContextWindowConfig();
    const client = getSupabaseClient();
    const { data, error } = await client
      .from('meridian_usage_events')
      .select(
        'input_tokens, output_tokens, total_tokens, cache_creation_input_tokens, cache_read_input_tokens, total_cost_usd'
      )
      .eq('chat_id', chatId);

    if (error) {
      throw error;
    }

    const rows = data ?? [];
    let inputTokens = 0;
    let outputTokens = 0;
    let totalTokens = 0;
    let cacheCreationInputTokens = 0;
    let cacheReadInputTokens = 0;
    let totalCostUsd = 0;

    for (const row of rows) {
      inputTokens += Number(row.input_tokens) || 0;
      outputTokens += Number(row.output_tokens) || 0;
      totalTokens += Number(row.total_tokens) || 0;
      cacheCreationInputTokens += Number(row.cache_creation_input_tokens) || 0;
      cacheReadInputTokens += Number(row.cache_read_input_tokens) || 0;
      totalCostUsd += Number(row.total_cost_usd) || 0;
    }

    return {
      chatId,
      stepCount: rows.length,
      inputTokens,
      outputTokens,
      totalTokens,
      cacheCreationInputTokens,
      cacheReadInputTokens,
      totalCostUsd,
      // Display-only snapshot of the env/default budget (no provider clamp —
      // this aggregate has no AIConfig). maxChars is derived for the legacy UI
      // copy; the budget itself is token-denominated.
      contextWindow: {
        maxMessages: windowConfig.maxMessages,
        maxChars: windowConfig.maxTokens * CHARS_PER_TOKEN_ESTIMATE,
        estimatedMaxTokens: windowConfig.maxTokens,
        charsPerTokenEstimate: CHARS_PER_TOKEN_ESTIMATE,
      },
    };
  } catch (error) {
    logger.error('error aggregating chat usage', error, {
      op: 'getChatUsageAggregate',
      chatId,
    });
    throw error;
  }
}

/** Cost and token totals for ONE chat, from the same rows as the aggregate above. */
export interface ChatUsageTotals {
  chatId: string;
  stepCount: number;
  totalTokens: number;
  totalCostUsd: number;
}

/**
 * Totals per chat for MANY chats in one query.
 *
 * Same table, same columns and the same summing rules as
 * `getChatUsageAggregate` — this exists so a caller that needs the cost of
 * twenty conversations (a routine's run history, or its month-to-date spend
 * against a budget) does not issue twenty round trips, and so nobody is tempted
 * to re-implement the arithmetic in the app.
 *
 * Chats with no usage rows are simply absent from the map; a caller reads a
 * missing entry as zero.
 */
export async function getUsageTotalsByChat(
  chatIds: readonly string[]
): Promise<Map<string, ChatUsageTotals>> {
  const totals = new Map<string, ChatUsageTotals>();
  if (chatIds.length === 0) return totals;

  try {
    const client = getSupabaseClient();
    const { data, error } = await client
      .from('meridian_usage_events')
      .select('chat_id, total_tokens, total_cost_usd')
      .in('chat_id', [...chatIds]);

    if (error) throw error;

    for (const row of data ?? []) {
      const chatId = String(row.chat_id);
      const current =
        totals.get(chatId) ?? { chatId, stepCount: 0, totalTokens: 0, totalCostUsd: 0 };
      current.stepCount += 1;
      current.totalTokens += Number(row.total_tokens) || 0;
      current.totalCostUsd += Number(row.total_cost_usd) || 0;
      totals.set(chatId, current);
    }
    return totals;
  } catch (error) {
    logger.error('error aggregating usage by chat', error, {
      op: 'getUsageTotalsByChat',
      chatCount: chatIds.length,
    });
    throw error;
  }
}
